/**
 * useActivitySummary — per-BU activity snapshot.
 *
 * Polls `GET /api/platform/v1/activity/summary` every 60 s while the
 * document is visible. Falls back to cached data on error so the UI
 * never clears on a transient failure.
 */

import * as React from "react";

import { PLATFORM_API, apiGetJSON } from "../lib/api";
import { useAuth } from "../providers/AuthProvider";

export interface ActivityEntry {
  lastActivityAt: string | null;
  unreadCount: number;
}

export interface ActivitySummary {
  user: { id: number };
  bus: Record<string, ActivityEntry>;
  generatedAt: string;
}

const POLL_INTERVAL_MS = 60_000;

interface UseActivitySummaryResult {
  data: ActivitySummary | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useActivitySummary(): UseActivitySummaryResult {
  const { token } = useAuth();
  const [data, setData] = React.useState<ActivitySummary | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [refetchTick, setRefetchTick] = React.useState(0);

  const fetchOnce = React.useCallback(
    async (signal: AbortSignal) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const result = await apiGetJSON<ActivitySummary>(
          `${PLATFORM_API}/activity/summary`,
          { signal },
        );
        setData(result);
      } catch (err: unknown) {
        if ((err as { name?: string }).name === "AbortError") return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  React.useEffect(() => {
    if (!token) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    void fetchOnce(controller.signal);

    // Re-fetch on visibility change (tab refocus).
    function onVisible() {
      if (document.visibilityState === "visible") {
        void fetchOnce(controller.signal);
      }
    }
    document.addEventListener("visibilitychange", onVisible);

    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void fetchOnce(controller.signal);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      controller.abort();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, fetchOnce, refetchTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = React.useCallback(() => setRefetchTick((t) => t + 1), []);

  return { data, loading, error, refetch };
}
